import React from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import Navbar from '../../../components/Navbar';
import PageWithAsyncData from '../../../components/PageWithAsyncData';
import {
  FullScreenLayout,
  TopBar,
  PageTitle,
  TableWrapper,
  EmptyStateText,
} from '../../../components/Admin/AdminLayout';
import { ROUTES } from '../../../routing';
import { useCmsGetProjects } from '../../../hooks/use-api/admin';
import { createColumnHelper } from '@tanstack/react-table';
import Table from '../../../components/Table/CompactTable';
import type { CmsProjectListItem } from '../../../api/admin';
import {
  FiltersHorizontalWrapper,
  TextFilterPill,
  SingleSelectFilterPill,
} from '../../../components/elements/Filter';
import day from 'dayjs';
import CMSPageWrapper, {
  SmallButton,
} from '../../../components/Admin/CMSPageWrapper';
import { CMS_SIDEBAR_PAGES } from '../../../components/Admin/CMSSideBar';

const SecondaryText = styled.span`
  color: ${({ theme }) => theme.colors.textRegular};
`;

const StatusBadge = styled.span<{ isLive: boolean }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ isLive, theme }) =>
    isLive ? theme.colors.textSuccess + '20' : theme.colors.textRegular + '20'};
  color: ${({ isLive, theme }) =>
    isLive ? theme.colors.textSuccess : theme.colors.textRegular};
`;

const columnHelper = createColumnHelper<CmsProjectListItem>();

export default function ProjectsList() {
  const { data: projects, isLoading, error, refetch } = useCmsGetProjects();
  const history = useHistory();

  const [nameFilter, setNameFilter] = React.useState('');
  const [statusFilter, setStatusFilter] =
    React.useState<'ALL' | 'LIVE' | 'ARCHIVED'>('ALL');

  const filteredProjects = React.useMemo(() => {
    let filtered = projects ?? [];
    const trimmed = nameFilter.trim().toLowerCase();
    if (trimmed) {
      filtered = filtered.filter((p) => p.name.toLowerCase().includes(trimmed));
    }
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter((p) => p.publishStatus === statusFilter);
    }
    return filtered;
  }, [projects, nameFilter, statusFilter]);

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: ({ getValue }) => (
          <span style={{ fontWeight: 500 }}>{getValue()}</span>
        ),
      }),
      columnHelper.accessor('slug', {
        header: 'Slug',
        cell: ({ getValue }) => <SecondaryText>{getValue()}</SecondaryText>,
      }),
      columnHelper.accessor('topic', {
        header: 'Topic',
        cell: ({ getValue }) => <SecondaryText>{getValue()}</SecondaryText>,
      }),
      columnHelper.accessor('questionsCount', {
        header: 'Num. Exercises',
        cell: ({ getValue }) => <SecondaryText>{getValue()}</SecondaryText>,
      }),
      columnHelper.accessor('publishStatus', {
        header: 'Status',
        cell: ({ getValue }) => {
          const isLive = getValue() === 'LIVE';
          return (
            <StatusBadge isLive={isLive}>
              {isLive ? 'Live' : 'Archived'}
            </StatusBadge>
          );
        },
      }),
      columnHelper.accessor((row) => new Date(row.updatedAt).getTime(), {
        id: 'updatedAt',
        header: 'Last Updated',
        cell: ({ row }) => (
          <SecondaryText>
            {day(row.original.updatedAt).format('DD/MM/YYYY HH:mm')}
          </SecondaryText>
        ),
      }),
    ],
    []
  );

  return (
    <PageWithAsyncData
      isLoading={isLoading}
      error={error}
      retry={refetch}
      noScroll
    >
      <Navbar
        breadCrumbs={[
          { label: 'Admin Dashboard', url: ROUTES.ADMIN.HOME.getPath() },
          { label: 'Projects', url: null },
        ]}
      />
      <CMSPageWrapper
        currentPage={CMS_SIDEBAR_PAGES.PROJECTS}
        shouldAddEmptyDiv={false}
      >
        <FullScreenLayout>
          <PageTitle>Projects</PageTitle>
          <TopBar>
            <FiltersHorizontalWrapper style={{ marginBottom: 0, flex: 1 }}>
              <TextFilterPill
                label="Name"
                value={nameFilter}
                placeholder="Filter by name..."
                onChange={setNameFilter}
              />
              <SingleSelectFilterPill
                label="Status"
                value={statusFilter}
                options={[
                  { label: 'All', value: 'ALL' },
                  { label: 'Live', value: 'LIVE' },
                  { label: 'Archived', value: 'ARCHIVED' },
                ]}
                onChange={(v) =>
                  setStatusFilter(v as 'ALL' | 'LIVE' | 'ARCHIVED')
                }
                shouldHideClear
              />
            </FiltersHorizontalWrapper>
            <SmallButton
              label="Create New"
              type="primary"
              faIcon="fa-solid fa-plus"
              showIconBeforeLabel
              onClick={() =>
                history.push(ROUTES.ADMIN.CMS_PROJECT_CREATE.getPath())
              }
            />
          </TopBar>
          {filteredProjects.length === 0 ? (
            <EmptyStateText>
              {nameFilter
                ? 'No projects match the current filter.'
                : 'No projects yet.'}
            </EmptyStateText>
          ) : (
            <TableWrapper>
              <Table
                data={filteredProjects}
                getRowId={(row) => row.slug}
                columns={columns}
                className="projects-table"
                getRowProps={(row) => ({
                  onClick: () =>
                    history.push(
                      ROUTES.ADMIN.CMS_PROJECT_EDIT.getPath({
                        slug: row.original.slug,
                      })
                    ),
                })}
              />
            </TableWrapper>
          )}
        </FullScreenLayout>
      </CMSPageWrapper>
    </PageWithAsyncData>
  );
}
